import { Schema } from "effect";
import {
  CalendarEventIdSchema,
  CalendarExternalEventIdSchema,
  CalendarOperationIdSchema,
  CalendarSyncJobIdSchema,
  ConnectionIdSchema,
  ProjectIdSchema,
  RequestIdSchema,
  ThreadIdSchema,
  UserIdSchema,
  WorkspaceIdSchema,
} from "./ids";
import {
  CanonicalIdentifierSchema,
  IanaTimeZoneSchema,
  NonNegativeSafeIntegerSchema,
  PapersFlowBoundaryParseOptions,
  toPapersFlowStandardSchema,
  toPapersFlowValidationSchema,
} from "./standard";
import { utf8ByteLength } from "./utf8";

export const CALENDAR_TITLE_MAX_BYTES = 240;
export const CALENDAR_LOCATION_MAX_BYTES = 1_024;
export const CALENDAR_NOTES_MAX_BYTES = 16 * 1_024;
export const CALENDAR_ATTENDEE_LIMIT = 200;
export const CALENDAR_EVENT_CONTENT_MAX_BYTES = 128 * 1_024;
export const CALENDAR_RANGE_MAX_DAYS = 400;
export const CALENDAR_EVENT_MAX_DURATION_MS = 400 * 86_400_000;
export const CALENDAR_RANGE_ITEM_LIMIT = 5_000;
export const CALENDAR_RANGE_SOURCE_LIMIT = 102;
export const CALENDAR_RANGE_RESULT_MAX_BYTES = 8 * 1_024 * 1_024;
export const CALENDAR_OPERATION_APPROVAL_TTL_MS = 15 * 60 * 1_000;
export const CALENDAR_SYNC_EVENT_LIMIT = 1_000;
export const CALENDAR_SYNC_INTERNAL_MAX_BYTES = 8 * 1_024 * 1_024;

const PositiveSafeIntegerSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(1),
  Schema.isLessThanOrEqualTo(Number.MAX_SAFE_INTEGER),
);

function boundedTrimmedText(maximumBytes: number, required: boolean) {
  return Schema.String.check(
    Schema.makeFilter(
      (value) =>
        (value === value.trim() &&
          (!required || value.length > 0) &&
          utf8ByteLength(value) <= maximumBytes) ||
        `must be trimmed${required ? ", non-empty," : ","} and at most ${maximumBytes} UTF-8 bytes`,
    ),
  );
}

const CalendarTimestampSchema = NonNegativeSafeIntegerSchema;
const CalendarSecondTimestampSchema = CalendarTimestampSchema.check(
  Schema.makeFilter((timestamp) => timestamp % 1_000 === 0 || "must use whole-second precision"),
);
export const CalendarRevisionSchema = PositiveSafeIntegerSchema;
export const CalendarTitleSchema = boundedTrimmedText(CALENDAR_TITLE_MAX_BYTES, true);
export const CalendarLocationSchema = boundedTrimmedText(CALENDAR_LOCATION_MAX_BYTES, false);
export const CalendarNotesSchema = boundedTrimmedText(CALENDAR_NOTES_MAX_BYTES, false);
export const CalendarScopeSchema = Schema.Literals(["workspace", "project"]);
export const CalendarProviderSchema = Schema.Literals(["google-calendar", "outlook", "icloud"]);
export const CalendarSourceSchema = Schema.Literals([
  "papersflow",
  "google-calendar",
  "outlook",
  "icloud",
]);
export const CalendarColorSchema = Schema.String.check(
  Schema.isMaxLength(32),
  Schema.makeFilter(
    (value) => /^#[0-9a-f]{6}$/u.test(value) || "must be a lowercase six-digit hex color",
  ),
);
export const CalendarTimeZoneSchema = IanaTimeZoneSchema.check(
  Schema.makeFilter((timeZone) => {
    try {
      Intl.DateTimeFormat("en-US", { timeZone }).resolvedOptions();
      return true;
    } catch {
      return "must be a supported IANA time-zone name";
    }
  }),
);
export const CalendarAttendeeResponseSchema = Schema.Literals([
  "needs_action",
  "accepted",
  "declined",
  "tentative",
]);
export const CalendarEmailSchema = Schema.Trim.check(
  Schema.isMinLength(3),
  Schema.isMaxLength(320),
  Schema.makeFilter(
    (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(value) || "must be an email address",
  ),
);

export const CalendarAttendeeSchema = Schema.Struct({
  email: CalendarEmailSchema,
  displayName: Schema.optionalKey(boundedTrimmedText(240, true)),
  response: CalendarAttendeeResponseSchema,
  optional: Schema.Boolean,
});
export const CalendarInvitationResponseSchema = Schema.Struct({
  response: CalendarAttendeeResponseSchema,
});

export const CalendarRecurrenceFrequencySchema = Schema.Literals([
  "daily",
  "weekly",
  "monthly",
  "yearly",
]);
export const CalendarWeekdaySchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(0),
  Schema.isLessThanOrEqualTo(6),
);
export const CalendarRecurrenceSchema = Schema.Struct({
  frequency: CalendarRecurrenceFrequencySchema,
  interval: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1), Schema.isLessThanOrEqualTo(99)),
  weekdays: Schema.optionalKey(
    Schema.Array(CalendarWeekdaySchema).check(
      Schema.isMinLength(1),
      Schema.isMaxLength(7),
      Schema.makeFilter((weekdays) => {
        const ascending = weekdays.every((weekday, index) => {
          const previous = weekdays[index - 1];
          return previous === undefined || weekday > previous;
        });
        return ascending || "weekdays must be unique and strictly ascending";
      }),
    ),
  ),
  monthDay: Schema.optionalKey(
    Schema.Int.check(Schema.isGreaterThanOrEqualTo(1), Schema.isLessThanOrEqualTo(31)),
  ),
  until: Schema.optionalKey(CalendarSecondTimestampSchema),
}).check(
  Schema.makeFilter((recurrence) => {
    if (recurrence.weekdays !== undefined && recurrence.frequency !== "weekly") {
      return "weekdays are available only for weekly recurrence";
    }
    if (recurrence.monthDay !== undefined && recurrence.frequency !== "monthly") {
      return "monthDay is available only for monthly recurrence";
    }
    return true;
  }),
);

export const CalendarPreservedConferenceSchema = Schema.Struct({
  label: boundedTrimmedText(160, true),
  url: Schema.String.check(
    Schema.isMaxLength(2_048),
    Schema.makeFilter(
      (value) => /^https:\/\/[^\s/?#@]+(?:[/?#]|$)/iu.test(value) || "must be an HTTPS URL",
    ),
  ),
});
export const CalendarPreservedReminderSchema = Schema.Struct({
  method: Schema.Literals(["notification", "email", "other"]),
  minutesBefore: NonNegativeSafeIntegerSchema.check(Schema.isLessThanOrEqualTo(525_600)),
});

const CalendarTimedFieldsSchema = Schema.Struct({
  startAt: CalendarTimestampSchema,
  endAt: CalendarTimestampSchema,
  allDay: Schema.Boolean,
  timeZone: CalendarTimeZoneSchema,
}).check(
  Schema.makeFilter((value) => value.endAt > value.startAt || "endAt must be later than startAt"),
);

function localCalendarParts(timestamp: number, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-CA-u-ca-gregory-hc-h23", {
    timeZone,
    calendar: "gregory",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3,
    hourCycle: "h23",
  }).formatToParts(timestamp);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value;
  return {
    year: value("year"),
    month: value("month"),
    day: value("day"),
    weekday: value("weekday"),
    hour: value("hour"),
    minute: value("minute"),
    second: value("second"),
    fractionalSecond: value("fractionalSecond"),
  };
}

function localMidnightDate(timestamp: number, timeZone: string): string | undefined {
  const parts = localCalendarParts(timestamp, timeZone);
  if (
    parts.hour !== "00" ||
    parts.minute !== "00" ||
    parts.second !== "00" ||
    parts.fractionalSecond !== "000"
  ) {
    return undefined;
  }
  return parts.year && parts.month && parts.day
    ? `${parts.year}-${parts.month}-${parts.day}`
    : undefined;
}

function isLocalInclusiveEndOfDay(timestamp: number, timeZone: string): boolean {
  const parts = localCalendarParts(timestamp, timeZone);
  return (
    parts.hour === "23" &&
    parts.minute === "59" &&
    parts.second === "59" &&
    parts.fractionalSecond === "000"
  );
}

interface CalendarTemporalValue {
  readonly startAt: number;
  readonly endAt: number;
  readonly allDay: boolean;
  readonly timeZone: string;
  readonly recurrence?: {
    readonly frequency: "daily" | "weekly" | "monthly" | "yearly";
    readonly weekdays?: readonly number[];
    readonly monthDay?: number;
    readonly until?: number;
  };
}

function calendarRecurrenceTemporalIssue(value: CalendarTemporalValue) {
  const recurrence = value.recurrence;
  if (recurrence === undefined) return true;
  if (recurrence.until !== undefined && recurrence.until < value.startAt) {
    return "recurrence until must not precede startAt";
  }
  if (
    recurrence.until !== undefined &&
    !isLocalInclusiveEndOfDay(recurrence.until, value.timeZone)
  ) {
    return "recurrence until must be the inclusive local end-of-day (23:59:59)";
  }
  const startParts = localCalendarParts(value.startAt, value.timeZone);
  const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(
    startParts.weekday ?? "",
  );
  if (
    recurrence.frequency === "weekly" &&
    recurrence.weekdays !== undefined &&
    !recurrence.weekdays.includes(weekday)
  ) {
    return "weekly recurrence weekdays must include the DTSTART local weekday";
  }
  if (
    recurrence.frequency === "monthly" &&
    recurrence.monthDay !== undefined &&
    recurrence.monthDay !== Number(startParts.day)
  ) {
    return "monthly recurrence monthDay must match the DTSTART local day";
  }
  return true;
}

function calendarAllDayTemporalIssue(value: CalendarTemporalValue) {
  if (!value.allDay) return true;
  const startDate = localMidnightDate(value.startAt, value.timeZone);
  const endDate = localMidnightDate(value.endAt, value.timeZone);
  if (startDate === undefined || endDate === undefined) {
    return "all-day startAt and endAt must be exact local midnight";
  }
  return endDate > startDate || "all-day endAt must be an exclusive later local date";
}

function calendarTemporalIssue(value: CalendarTemporalValue) {
  if (value.endAt <= value.startAt) return "endAt must be later than startAt";
  if (value.endAt - value.startAt > CALENDAR_EVENT_MAX_DURATION_MS) {
    return "calendar event duration cannot exceed 400 days";
  }
  const recurrenceIssue = calendarRecurrenceTemporalIssue(value);
  return recurrenceIssue === true ? calendarAllDayTemporalIssue(value) : recurrenceIssue;
}

interface CalendarContentValue {
  readonly title: string;
  readonly location?: string;
  readonly notes?: string;
  readonly attendees: readonly {
    readonly email: string;
    readonly displayName?: string;
    readonly response: string;
    readonly optional: boolean;
  }[];
  readonly recurrence?: {
    readonly frequency: string;
    readonly interval: number;
    readonly weekdays?: readonly number[];
    readonly monthDay?: number;
    readonly until?: number;
  };
  readonly conference?: { readonly label: string; readonly url: string };
  readonly reminders: readonly { readonly method: string; readonly minutesBefore: number }[];
}

function calendarEventIssue(value: CalendarTemporalValue & CalendarContentValue) {
  const temporalIssue = calendarTemporalIssue(value);
  if (temporalIssue !== true) return temporalIssue;
  const content = {
    title: value.title,
    ...(value.location === undefined ? {} : { location: value.location }),
    ...(value.notes === undefined ? {} : { notes: value.notes }),
    attendees: value.attendees,
    ...(value.recurrence === undefined ? {} : { recurrence: value.recurrence }),
    ...(value.conference === undefined ? {} : { conference: value.conference }),
    reminders: value.reminders,
  };
  return (
    utf8ByteLength(JSON.stringify(content)) <= CALENDAR_EVENT_CONTENT_MAX_BYTES ||
    `calendar event content cannot exceed ${CALENDAR_EVENT_CONTENT_MAX_BYTES} UTF-8 bytes`
  );
}

const CalendarContentFields = {
  title: CalendarTitleSchema,
  location: Schema.optionalKey(CalendarLocationSchema),
  notes: Schema.optionalKey(CalendarNotesSchema),
  attendees: Schema.Array(CalendarAttendeeSchema).check(
    Schema.isMaxLength(CALENDAR_ATTENDEE_LIMIT),
    Schema.makeFilter(
      (attendees) =>
        new Set(attendees.map((attendee) => attendee.email.toLowerCase())).size ===
          attendees.length || "attendee emails must be unique",
    ),
  ),
  recurrence: Schema.optionalKey(CalendarRecurrenceSchema),
  conference: Schema.optionalKey(CalendarPreservedConferenceSchema),
  reminders: Schema.Array(CalendarPreservedReminderSchema).check(Schema.isMaxLength(20)),
} as const;

function calendarScopeIssue(value: {
  readonly scope: CalendarScope;
  readonly projectId?: unknown;
}) {
  if (value.scope === "project") {
    return value.projectId !== undefined || "a project event requires projectId";
  }
  return value.projectId === undefined || "a workspace event cannot contain projectId";
}

export const CalendarEventDraftSchema = Schema.Struct({
  scope: CalendarScopeSchema,
  projectId: Schema.optionalKey(ProjectIdSchema),
  ...CalendarTimedFieldsSchema.fields,
  ...CalendarContentFields,
}).check(
  Schema.makeFilter((draft) => {
    const temporalIssue = calendarEventIssue(draft);
    if (temporalIssue !== true) return temporalIssue;
    return calendarScopeIssue(draft);
  }),
);

export const CalendarEventSchema = Schema.Struct({
  version: Schema.Literal("calendar-event/v1"),
  eventId: CalendarEventIdSchema,
  workspaceId: WorkspaceIdSchema,
  scope: CalendarScopeSchema,
  projectId: Schema.optionalKey(ProjectIdSchema),
  ...CalendarTimedFieldsSchema.fields,
  ...CalendarContentFields,
  revision: CalendarRevisionSchema,
  createdBy: UserIdSchema,
  updatedBy: UserIdSchema,
  createdAt: CalendarTimestampSchema,
  updatedAt: CalendarTimestampSchema,
  archivedAt: Schema.optionalKey(CalendarTimestampSchema),
}).check(
  Schema.makeFilter((event) => {
    const temporalIssue = calendarEventIssue(event);
    if (temporalIssue !== true) return temporalIssue;
    return calendarScopeIssue(event);
  }),
);

export const CalendarExternalEventSchema = Schema.Struct({
  version: Schema.Literal("calendar-external-event/v1"),
  externalEventId: CalendarExternalEventIdSchema,
  workspaceId: WorkspaceIdSchema,
  ownerUserId: UserIdSchema,
  connectionId: ConnectionIdSchema,
  sourceId: CanonicalIdentifierSchema,
  provider: CalendarProviderSchema,
  accountLabel: boundedTrimmedText(240, true),
  calendarExternalId: CanonicalIdentifierSchema,
  calendarLabel: boundedTrimmedText(240, true),
  providerEventId: CanonicalIdentifierSchema,
  providerEtag: Schema.optionalKey(CanonicalIdentifierSchema),
  readOnly: Schema.Boolean,
  invitationResponse: Schema.optionalKey(CalendarInvitationResponseSchema),
  unsupportedRecurrence: Schema.optionalKey(boundedTrimmedText(1_024, true)),
  ...CalendarTimedFieldsSchema.fields,
  ...CalendarContentFields,
  providerUpdatedAt: Schema.optionalKey(CalendarTimestampSchema),
  synchronizedAt: CalendarTimestampSchema,
  deletedAt: Schema.optionalKey(CalendarTimestampSchema),
}).check(Schema.makeFilter(calendarEventIssue));

export const CalendarPublicationStateSchema = Schema.Literals([
  "local",
  "approval_required",
  "publishing",
  "published",
  "drifted",
  "failed",
]);

export const CalendarRangeItemSchema = Schema.Struct({
  eventId: CanonicalIdentifierSchema,
  sourceId: CanonicalIdentifierSchema,
  source: CalendarSourceSchema,
  sourceLabel: boundedTrimmedText(240, true),
  sourceColor: CalendarColorSchema,
  writable: Schema.Boolean,
  scope: Schema.Literals(["workspace", "project", "private"]),
  projectId: Schema.optionalKey(ProjectIdSchema),
  ...CalendarTimedFieldsSchema.fields,
  ...CalendarContentFields,
  occurrenceStartAt: Schema.optionalKey(CalendarTimestampSchema),
  revision: Schema.optionalKey(CalendarRevisionSchema),
  publicationState: Schema.optionalKey(CalendarPublicationStateSchema),
  externalEventId: Schema.optionalKey(CalendarExternalEventIdSchema),
  invitationResponse: Schema.optionalKey(CalendarInvitationResponseSchema),
  unsupportedRecurrence: Schema.optionalKey(boundedTrimmedText(1_024, true)),
}).check(Schema.makeFilter(calendarEventIssue));

export const CalendarSourceDescriptorSchema = Schema.Struct({
  sourceId: CanonicalIdentifierSchema,
  provider: CalendarSourceSchema,
  label: boundedTrimmedText(240, true),
  accountLabel: Schema.optionalKey(boundedTrimmedText(240, true)),
  color: CalendarColorSchema,
  status: Schema.Literals(["connected", "unavailable"]),
  writable: Schema.Boolean,
});

export const CalendarRangeRequestSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: Schema.optionalKey(ProjectIdSchema),
  rangeStartAt: CalendarTimestampSchema,
  rangeEndAt: CalendarTimestampSchema,
}).check(
  Schema.makeFilter((request) => {
    if (request.rangeEndAt <= request.rangeStartAt) return "rangeEndAt must follow rangeStartAt";
    return (
      request.rangeEndAt - request.rangeStartAt <= CALENDAR_RANGE_MAX_DAYS * 86_400_000 ||
      `calendar range cannot exceed ${CALENDAR_RANGE_MAX_DAYS} days`
    );
  }),
);

export const CalendarRangeResultSchema = Schema.Struct({
  workspaceTimeZone: CalendarTimeZoneSchema,
  canWrite: Schema.Boolean,
  canPublish: Schema.Boolean,
  sources: Schema.Array(CalendarSourceDescriptorSchema).check(
    Schema.isMaxLength(CALENDAR_RANGE_SOURCE_LIMIT),
  ),
  items: Schema.Array(CalendarRangeItemSchema).check(Schema.isMaxLength(CALENDAR_RANGE_ITEM_LIMIT)),
}).check(
  Schema.makeFilter(
    (result) =>
      utf8ByteLength(JSON.stringify(result)) <= CALENDAR_RANGE_RESULT_MAX_BYTES ||
      `calendar range result cannot exceed ${CALENDAR_RANGE_RESULT_MAX_BYTES} UTF-8 bytes`,
  ),
);

export const CalendarProviderTargetSchema = Schema.Struct({
  provider: CalendarProviderSchema,
  connectionId: ConnectionIdSchema,
  accountLabel: boundedTrimmedText(240, true),
  calendarExternalId: CanonicalIdentifierSchema,
  calendarLabel: boundedTrimmedText(240, true),
});

export const CalendarApprovalEventSnapshotSchema = Schema.Struct({
  ...CalendarTimedFieldsSchema.fields,
  ...CalendarContentFields,
}).check(Schema.makeFilter(calendarEventIssue));

const CalendarApprovalRecipientsSchema = Schema.Array(CalendarEmailSchema).check(
  Schema.isMaxLength(CALENDAR_ATTENDEE_LIMIT),
  Schema.makeFilter(
    (emails) =>
      new Set(emails.map((email) => email.toLowerCase())).size === emails.length ||
      "recipient emails must be unique",
  ),
);

export const CalendarPublishOperationInputSchema = Schema.Struct({
  kind: Schema.Literal("publish"),
  eventId: CalendarEventIdSchema,
  expectedRevision: CalendarRevisionSchema,
  snapshot: CalendarApprovalEventSnapshotSchema,
  target: CalendarProviderTargetSchema,
  recipients: CalendarApprovalRecipientsSchema,
});
export const CalendarUpdateDeliveryOperationInputSchema = Schema.Struct({
  kind: Schema.Literal("update_delivery"),
  eventId: CalendarEventIdSchema,
  providerEventId: CanonicalIdentifierSchema,
  expectedRevision: CalendarRevisionSchema,
  expectedProviderEtag: CanonicalIdentifierSchema,
  replacement: CalendarEventDraftSchema,
  target: CalendarProviderTargetSchema,
  recipients: CalendarApprovalRecipientsSchema,
});
export const CalendarCancelDeliveryOperationInputSchema = Schema.Struct({
  kind: Schema.Literal("cancel_delivery"),
  eventId: CalendarEventIdSchema,
  providerEventId: CanonicalIdentifierSchema,
  expectedRevision: CalendarRevisionSchema,
  expectedProviderEtag: CanonicalIdentifierSchema,
  snapshot: CalendarApprovalEventSnapshotSchema,
  target: CalendarProviderTargetSchema,
  recipients: CalendarApprovalRecipientsSchema,
});
export const CalendarRespondInvitationOperationInputSchema = Schema.Struct({
  kind: Schema.Literal("respond_invitation"),
  externalEventId: CalendarExternalEventIdSchema,
  expectedProviderEtag: CanonicalIdentifierSchema,
  response: Schema.Literals(["accepted", "declined", "tentative"]),
  snapshot: CalendarApprovalEventSnapshotSchema,
  target: CalendarProviderTargetSchema,
});
export const CalendarImportOperationInputSchema = Schema.Struct({
  kind: Schema.Literal("import_external"),
  externalEventId: CalendarExternalEventIdSchema,
  scope: CalendarScopeSchema,
  projectId: Schema.optionalKey(ProjectIdSchema),
  includeNotes: Schema.Boolean,
  includeAttendees: Schema.Boolean,
  includeConference: Schema.Boolean,
  snapshot: CalendarEventDraftSchema,
}).check(
  Schema.makeFilter((input) => {
    if (input.scope === "project" && input.projectId === undefined) return "projectId is required";
    if (input.scope === "workspace" && input.projectId !== undefined) {
      return "workspace imports cannot contain projectId";
    }
    if (input.snapshot.scope !== input.scope || input.snapshot.projectId !== input.projectId) {
      return "import snapshot scope must match the approved target";
    }
    if (!input.includeNotes && input.snapshot.notes !== undefined) {
      return "an import without notes cannot contain snapshot notes";
    }
    if (!input.includeAttendees && input.snapshot.attendees.length > 0) {
      return "an import without attendees cannot contain snapshot attendees";
    }
    if (!input.includeConference && input.snapshot.conference !== undefined) {
      return "an import without a conference cannot contain a snapshot conference";
    }
    return true;
  }),
);

export const CalendarOperationInputSchema = Schema.Union([
  CalendarPublishOperationInputSchema,
  CalendarUpdateDeliveryOperationInputSchema,
  CalendarCancelDeliveryOperationInputSchema,
  CalendarRespondInvitationOperationInputSchema,
  CalendarImportOperationInputSchema,
]);
export const CalendarOperationStatusSchema = Schema.Literals([
  "pending_approval",
  "approved",
  "executing",
  "reconciling",
  "succeeded",
  "rejected",
  "failed",
  "expired",
]);
export const CalendarOperationSchema = Schema.Struct({
  version: Schema.Literal("calendar-operation/v1"),
  operationId: CalendarOperationIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: Schema.optionalKey(ProjectIdSchema),
  threadId: Schema.optionalKey(ThreadIdSchema),
  requestId: RequestIdSchema,
  requestedBy: UserIdSchema,
  status: CalendarOperationStatusSchema,
  input: CalendarOperationInputSchema,
  createdAt: CalendarTimestampSchema,
  approvalExpiresAt: CalendarTimestampSchema,
  resolvedBy: Schema.optionalKey(UserIdSchema),
  resolvedAt: Schema.optionalKey(CalendarTimestampSchema),
  executionStartedAt: Schema.optionalKey(CalendarTimestampSchema),
  completedAt: Schema.optionalKey(CalendarTimestampSchema),
  publicErrorCode: Schema.optionalKey(
    Schema.Literals([
      "CALENDAR_PROVIDER_UNAVAILABLE",
      "CALENDAR_PROVIDER_CONFLICT",
      "CALENDAR_CONNECTION_REQUIRED",
      "CALENDAR_EXECUTION_UNCERTAIN",
      "CALENDAR_APPROVAL_STALE",
    ]),
  ),
}).check(
  Schema.makeFilter((operation) => {
    if (
      operation.input.kind === "import_external" &&
      operation.projectId !== operation.input.projectId
    ) {
      return "calendar import operation project must match its approved snapshot";
    }
    if (operation.approvalExpiresAt <= operation.createdAt) {
      return "approvalExpiresAt must follow createdAt";
    }
    if (operation.approvalExpiresAt - operation.createdAt > CALENDAR_OPERATION_APPROVAL_TTL_MS) {
      return `approval cannot exceed ${CALENDAR_OPERATION_APPROVAL_TTL_MS} milliseconds`;
    }
    if (operation.status === "pending_approval") {
      return (
        (operation.resolvedBy === undefined && operation.resolvedAt === undefined) ||
        "a pending operation cannot contain resolution fields"
      );
    }
    if (operation.status === "rejected") {
      return (
        (operation.resolvedBy !== undefined && operation.resolvedAt !== undefined) ||
        "a rejected operation requires resolution fields"
      );
    }
    return true;
  }),
);

export const CalendarSearchToolInputSchema = Schema.Struct({
  query: Schema.optionalKey(boundedTrimmedText(500, true)),
  rangeStartAt: CalendarTimestampSchema,
  rangeEndAt: CalendarTimestampSchema,
  scope: Schema.Literals(["workspace_and_project", "project"]),
}).check(
  Schema.makeFilter((request) => {
    if (request.rangeEndAt <= request.rangeStartAt) return "rangeEndAt must follow rangeStartAt";
    return (
      request.rangeEndAt - request.rangeStartAt <= CALENDAR_RANGE_MAX_DAYS * 86_400_000 ||
      `calendar range cannot exceed ${CALENDAR_RANGE_MAX_DAYS} days`
    );
  }),
);
export const CalendarGetToolInputSchema = Schema.Struct({
  eventId: CanonicalIdentifierSchema,
});
export const CalendarProposeToolInputSchema = Schema.Struct({
  requestId: RequestIdSchema,
  input: CalendarOperationInputSchema,
});
export const CalendarSearchToolOutputSchema = Schema.Struct({
  events: Schema.Array(CalendarRangeItemSchema).check(Schema.isMaxLength(200)),
}).check(
  Schema.makeFilter(
    (result) =>
      utf8ByteLength(JSON.stringify(result)) <= CALENDAR_RANGE_RESULT_MAX_BYTES ||
      `calendar search result cannot exceed ${CALENDAR_RANGE_RESULT_MAX_BYTES} UTF-8 bytes`,
  ),
);
export const CalendarProposalToolOutputSchema = Schema.Struct({
  operationId: CalendarOperationIdSchema,
  status: Schema.Literal("pending_approval"),
  approvalExpiresAt: CalendarTimestampSchema,
});

const CalendarAgentToolContextFields = {
  requestId: RequestIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  threadId: ThreadIdSchema,
  actorUserId: UserIdSchema,
} as const;

export const CalendarSearchToolRequestSchema = Schema.Struct({
  ...CalendarAgentToolContextFields,
  input: CalendarSearchToolInputSchema,
});
export const CalendarGetToolRequestSchema = Schema.Struct({
  ...CalendarAgentToolContextFields,
  input: CalendarGetToolInputSchema,
});
export const CalendarProposeToolRequestSchema = Schema.Struct({
  ...CalendarAgentToolContextFields,
  input: CalendarProposeToolInputSchema,
});
export const CalendarGetToolOutputSchema = Schema.Struct({
  event: CalendarRangeItemSchema,
});

export const CalendarExecutionClaimSchema = Schema.Struct({
  operation: CalendarOperationSchema,
  nativeEvent: Schema.optionalKey(CalendarEventSchema),
  externalEvent: Schema.optionalKey(CalendarExternalEventSchema),
  providerTransport: Schema.Literals(["composio", "icloud-mcp"]),
  providerAccountId: CanonicalIdentifierSchema,
  providerUserId: CanonicalIdentifierSchema,
  leaseOwner: CanonicalIdentifierSchema,
  leaseGeneration: PositiveSafeIntegerSchema,
  leaseExpiresAt: CalendarTimestampSchema,
});
export const ClaimCalendarOperationRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  workerId: CanonicalIdentifierSchema,
  leaseMs: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(5_000),
    Schema.isLessThanOrEqualTo(120_000),
  ),
});
export const ClaimCalendarOperationResultSchema = Schema.NullOr(CalendarExecutionClaimSchema);
export const CompleteCalendarOperationRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  operationId: CalendarOperationIdSchema,
  workerId: CanonicalIdentifierSchema,
  leaseGeneration: PositiveSafeIntegerSchema,
  providerEventId: Schema.optionalKey(CanonicalIdentifierSchema),
  providerEtag: Schema.optionalKey(CanonicalIdentifierSchema),
  providerUpdatedAt: Schema.optionalKey(CalendarTimestampSchema),
});
export const FailCalendarOperationRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  operationId: CalendarOperationIdSchema,
  workerId: CanonicalIdentifierSchema,
  leaseGeneration: PositiveSafeIntegerSchema,
  outcome: Schema.Literals(["not_applied", "uncertain"]),
  publicErrorCode: Schema.Literals([
    "CALENDAR_PROVIDER_UNAVAILABLE",
    "CALENDAR_PROVIDER_CONFLICT",
    "CALENDAR_CONNECTION_REQUIRED",
    "CALENDAR_EXECUTION_UNCERTAIN",
    "CALENDAR_APPROVAL_STALE",
  ]),
});
export const ReconcileCalendarOperationRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  operationId: CalendarOperationIdSchema,
  workerId: CanonicalIdentifierSchema,
  leaseGeneration: PositiveSafeIntegerSchema,
  outcome: Schema.Literals(["applied", "not_applied"]),
  providerEventId: Schema.optionalKey(CanonicalIdentifierSchema),
  providerEtag: Schema.optionalKey(CanonicalIdentifierSchema),
  providerUpdatedAt: Schema.optionalKey(CalendarTimestampSchema),
});

export const CalendarSyncReasonSchema = Schema.Literals(["initial", "poll", "range"]);
export const CalendarSyncSourceSchema = Schema.Struct({
  calendarExternalId: CanonicalIdentifierSchema,
  label: boundedTrimmedText(240, true),
  color: CalendarColorSchema,
  readOnly: Schema.Boolean,
});
export const CalendarSyncExternalEventSchema = Schema.Struct({
  calendarExternalId: CanonicalIdentifierSchema,
  providerEventId: CanonicalIdentifierSchema,
  providerEtag: Schema.optionalKey(CanonicalIdentifierSchema),
  invitationResponse: Schema.optionalKey(CalendarInvitationResponseSchema),
  unsupportedRecurrence: Schema.optionalKey(boundedTrimmedText(1_024, true)),
  ...CalendarTimedFieldsSchema.fields,
  ...CalendarContentFields,
  providerUpdatedAt: Schema.optionalKey(CalendarTimestampSchema),
}).check(Schema.makeFilter(calendarEventIssue));
export const CalendarSyncDeletedEventSchema = Schema.Struct({
  calendarExternalId: CanonicalIdentifierSchema,
  providerEventId: CanonicalIdentifierSchema,
});
export const CalendarSyncSnapshotSchema = Schema.Struct({
  completeness: Schema.Literal("complete"),
  rangeStartAt: CalendarTimestampSchema,
  rangeEndAt: CalendarTimestampSchema,
  sources: Schema.Array(CalendarSyncSourceSchema).check(Schema.isMaxLength(100)),
  events: Schema.Array(CalendarSyncExternalEventSchema).check(
    Schema.isMaxLength(CALENDAR_SYNC_EVENT_LIMIT),
  ),
  deletedEvents: Schema.Array(CalendarSyncDeletedEventSchema).check(
    Schema.isMaxLength(CALENDAR_SYNC_EVENT_LIMIT),
  ),
}).check(
  Schema.makeFilter((snapshot) => {
    if (snapshot.rangeEndAt <= snapshot.rangeStartAt) {
      return "rangeEndAt must follow rangeStartAt";
    }
    if (snapshot.rangeEndAt - snapshot.rangeStartAt > CALENDAR_RANGE_MAX_DAYS * 86_400_000) {
      return `calendar range cannot exceed ${CALENDAR_RANGE_MAX_DAYS} days`;
    }
    if (snapshot.events.length + snapshot.deletedEvents.length > CALENDAR_SYNC_EVENT_LIMIT) {
      return `calendar synchronization snapshot cannot contain more than ${CALENDAR_SYNC_EVENT_LIMIT} provider event identities`;
    }
    if (utf8ByteLength(JSON.stringify(snapshot)) > CALENDAR_SYNC_INTERNAL_MAX_BYTES - 4 * 1_024) {
      return `calendar synchronization snapshot must fit the ${CALENDAR_SYNC_INTERNAL_MAX_BYTES}-byte internal request boundary`;
    }
    const sourceIds = new Set(snapshot.sources.map((source) => source.calendarExternalId));
    if (sourceIds.size !== snapshot.sources.length) return "calendar sources must be unique";
    if (snapshot.events.some((event) => !sourceIds.has(event.calendarExternalId))) {
      return "calendar events must reference a synchronized source";
    }
    if (snapshot.deletedEvents.some((event) => !sourceIds.has(event.calendarExternalId))) {
      return "deleted calendar events must reference a synchronized source";
    }
    const eventKeys = snapshot.events.map(
      (event) => `${event.calendarExternalId}\u0000${event.providerEventId}`,
    );
    const deletedKeys = snapshot.deletedEvents.map(
      (event) => `${event.calendarExternalId}\u0000${event.providerEventId}`,
    );
    const allKeys = [...eventKeys, ...deletedKeys];
    return new Set(allKeys).size === allKeys.length || "calendar provider events must be unique";
  }),
);
export const CalendarSyncJobClaimSchema = Schema.Struct({
  syncJobId: CalendarSyncJobIdSchema,
  workspaceId: WorkspaceIdSchema,
  ownerUserId: UserIdSchema,
  connectionId: ConnectionIdSchema,
  provider: Schema.Literals(["google-calendar", "outlook"]),
  providerAccountId: CanonicalIdentifierSchema,
  providerUserId: CanonicalIdentifierSchema,
  reason: CalendarSyncReasonSchema,
  rangeStartAt: CalendarTimestampSchema,
  rangeEndAt: CalendarTimestampSchema,
  leaseOwner: CanonicalIdentifierSchema,
  leaseGeneration: PositiveSafeIntegerSchema,
  leaseExpiresAt: CalendarTimestampSchema,
}).check(
  Schema.makeFilter((claim) => {
    if (claim.rangeEndAt <= claim.rangeStartAt) return "rangeEndAt must follow rangeStartAt";
    return (
      claim.rangeEndAt - claim.rangeStartAt <= CALENDAR_RANGE_MAX_DAYS * 86_400_000 ||
      `calendar range cannot exceed ${CALENDAR_RANGE_MAX_DAYS} days`
    );
  }),
);
export const ClaimCalendarSyncJobRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  workerId: CanonicalIdentifierSchema,
  leaseMs: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(5_000),
    Schema.isLessThanOrEqualTo(120_000),
  ),
});
export const ClaimCalendarSyncJobResultSchema = Schema.NullOr(CalendarSyncJobClaimSchema);
export const CompleteCalendarSyncJobRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  syncJobId: CalendarSyncJobIdSchema,
  workerId: CanonicalIdentifierSchema,
  leaseGeneration: PositiveSafeIntegerSchema,
  snapshot: CalendarSyncSnapshotSchema,
});
export const FailCalendarSyncJobRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  syncJobId: CalendarSyncJobIdSchema,
  workerId: CanonicalIdentifierSchema,
  leaseGeneration: PositiveSafeIntegerSchema,
  retryable: Schema.Boolean,
  publicErrorCode: Schema.Literals([
    "CALENDAR_PROVIDER_UNAVAILABLE",
    "CALENDAR_CONNECTION_REQUIRED",
    "CALENDAR_SYNC_INVALID_RESPONSE",
  ]),
});
export const CalendarSyncTransitionResultSchema = Schema.Struct({
  status: Schema.Literals(["pending", "completed", "failed"]),
});

export const CalendarEventStandardSchema = toPapersFlowValidationSchema(CalendarEventSchema);
export const CalendarExternalEventStandardSchema = toPapersFlowValidationSchema(
  CalendarExternalEventSchema,
);
export const CalendarOperationStandardSchema =
  toPapersFlowValidationSchema(CalendarOperationSchema);
export const CalendarSearchToolInputStandardSchema = toPapersFlowStandardSchema(
  CalendarSearchToolInputSchema,
);
export const CalendarGetToolInputStandardSchema = toPapersFlowStandardSchema(
  CalendarGetToolInputSchema,
);
export const CalendarProposeToolInputStandardSchema = toPapersFlowStandardSchema(
  CalendarProposeToolInputSchema,
);

export const decodeCalendarEvent = Schema.decodeUnknownSync(
  CalendarEventSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCalendarEventDraft = Schema.decodeUnknownSync(
  CalendarEventDraftSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeCalendarEvent = Schema.encodeSync(
  CalendarEventSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCalendarExternalEvent = Schema.decodeUnknownSync(
  CalendarExternalEventSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeCalendarExternalEvent = Schema.encodeSync(
  CalendarExternalEventSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCalendarRangeRequest = Schema.decodeUnknownSync(
  CalendarRangeRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCalendarRangeResult = Schema.decodeUnknownSync(
  CalendarRangeResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCalendarOperationInput = Schema.decodeUnknownSync(
  CalendarOperationInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCalendarOperation = Schema.decodeUnknownSync(
  CalendarOperationSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeCalendarOperation = Schema.encodeSync(
  CalendarOperationSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCalendarSearchToolInput = Schema.decodeUnknownSync(
  CalendarSearchToolInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCalendarGetToolInput = Schema.decodeUnknownSync(
  CalendarGetToolInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCalendarProposeToolInput = Schema.decodeUnknownSync(
  CalendarProposeToolInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCalendarSearchToolRequest = Schema.decodeUnknownSync(
  CalendarSearchToolRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeCalendarSearchToolRequest = Schema.encodeSync(
  CalendarSearchToolRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCalendarSearchToolOutput = Schema.decodeUnknownSync(
  CalendarSearchToolOutputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCalendarGetToolRequest = Schema.decodeUnknownSync(
  CalendarGetToolRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeCalendarGetToolRequest = Schema.encodeSync(
  CalendarGetToolRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCalendarGetToolOutput = Schema.decodeUnknownSync(
  CalendarGetToolOutputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCalendarProposeToolRequest = Schema.decodeUnknownSync(
  CalendarProposeToolRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeCalendarProposeToolRequest = Schema.encodeSync(
  CalendarProposeToolRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCalendarProposalToolOutput = Schema.decodeUnknownSync(
  CalendarProposalToolOutputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeClaimCalendarOperationRequest = Schema.decodeUnknownSync(
  ClaimCalendarOperationRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeClaimCalendarOperationRequest = Schema.encodeSync(
  ClaimCalendarOperationRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeClaimCalendarOperationResult = Schema.decodeUnknownSync(
  ClaimCalendarOperationResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeClaimCalendarOperationResult = Schema.encodeSync(
  ClaimCalendarOperationResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCompleteCalendarOperationRequest = Schema.decodeUnknownSync(
  CompleteCalendarOperationRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeCompleteCalendarOperationRequest = Schema.encodeSync(
  CompleteCalendarOperationRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeFailCalendarOperationRequest = Schema.decodeUnknownSync(
  FailCalendarOperationRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeFailCalendarOperationRequest = Schema.encodeSync(
  FailCalendarOperationRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeReconcileCalendarOperationRequest = Schema.decodeUnknownSync(
  ReconcileCalendarOperationRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeReconcileCalendarOperationRequest = Schema.encodeSync(
  ReconcileCalendarOperationRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCalendarSyncSnapshot = Schema.decodeUnknownSync(
  CalendarSyncSnapshotSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeCalendarSyncSnapshot = Schema.encodeSync(
  CalendarSyncSnapshotSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeClaimCalendarSyncJobRequest = Schema.decodeUnknownSync(
  ClaimCalendarSyncJobRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeClaimCalendarSyncJobRequest = Schema.encodeSync(
  ClaimCalendarSyncJobRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeClaimCalendarSyncJobResult = Schema.decodeUnknownSync(
  ClaimCalendarSyncJobResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeClaimCalendarSyncJobResult = Schema.encodeSync(
  ClaimCalendarSyncJobResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCompleteCalendarSyncJobRequest = Schema.decodeUnknownSync(
  CompleteCalendarSyncJobRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeCompleteCalendarSyncJobRequest = Schema.encodeSync(
  CompleteCalendarSyncJobRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeFailCalendarSyncJobRequest = Schema.decodeUnknownSync(
  FailCalendarSyncJobRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeFailCalendarSyncJobRequest = Schema.encodeSync(
  FailCalendarSyncJobRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCalendarSyncTransitionResult = Schema.decodeUnknownSync(
  CalendarSyncTransitionResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const areCalendarOperationInputsEquivalent = Schema.toEquivalence(
  CalendarOperationInputSchema,
);
export const areCalendarEventDraftsEquivalent = Schema.toEquivalence(CalendarEventDraftSchema);
export const areCalendarApprovalEventSnapshotsEquivalent = Schema.toEquivalence(
  CalendarApprovalEventSnapshotSchema,
);

export type CalendarScope = typeof CalendarScopeSchema.Type;
export type CalendarProvider = typeof CalendarProviderSchema.Type;
export type CalendarSource = typeof CalendarSourceSchema.Type;
export type CalendarAttendee = typeof CalendarAttendeeSchema.Type;
export type CalendarRecurrence = typeof CalendarRecurrenceSchema.Type;
export type CalendarEvent = typeof CalendarEventSchema.Type;
export type CalendarEventEncoded = typeof CalendarEventSchema.Encoded;
export type CalendarEventDraft = typeof CalendarEventDraftSchema.Type;
export type CalendarEventDraftEncoded = typeof CalendarEventDraftSchema.Encoded;
export type CalendarExternalEvent = typeof CalendarExternalEventSchema.Type;
export type CalendarExternalEventEncoded = typeof CalendarExternalEventSchema.Encoded;
export type CalendarRangeItem = typeof CalendarRangeItemSchema.Type;
export type CalendarRangeItemEncoded = typeof CalendarRangeItemSchema.Encoded;
export type CalendarSourceDescriptor = typeof CalendarSourceDescriptorSchema.Type;
export type CalendarRangeRequest = typeof CalendarRangeRequestSchema.Type;
export type CalendarRangeResult = typeof CalendarRangeResultSchema.Type;
export type CalendarProviderTarget = typeof CalendarProviderTargetSchema.Type;
export type CalendarApprovalEventSnapshot = typeof CalendarApprovalEventSnapshotSchema.Type;
export type CalendarOperationInput = typeof CalendarOperationInputSchema.Type;
export type CalendarOperationStatus = typeof CalendarOperationStatusSchema.Type;
export type CalendarOperation = typeof CalendarOperationSchema.Type;
export type CalendarOperationEncoded = typeof CalendarOperationSchema.Encoded;
export type CalendarSearchToolInput = typeof CalendarSearchToolInputSchema.Type;
export type CalendarGetToolInput = typeof CalendarGetToolInputSchema.Type;
export type CalendarProposeToolInput = typeof CalendarProposeToolInputSchema.Type;
export type CalendarSearchToolRequest = typeof CalendarSearchToolRequestSchema.Type;
export type CalendarGetToolRequest = typeof CalendarGetToolRequestSchema.Type;
export type CalendarProposeToolRequest = typeof CalendarProposeToolRequestSchema.Type;
export type CalendarSearchToolOutput = typeof CalendarSearchToolOutputSchema.Type;
export type CalendarGetToolOutput = typeof CalendarGetToolOutputSchema.Type;
export type CalendarProposalToolOutput = typeof CalendarProposalToolOutputSchema.Type;
export type CalendarExecutionClaim = typeof CalendarExecutionClaimSchema.Type;
export type ClaimCalendarOperationRequest = typeof ClaimCalendarOperationRequestSchema.Type;
export type ClaimCalendarOperationResult = typeof ClaimCalendarOperationResultSchema.Type;
export type CompleteCalendarOperationRequest = typeof CompleteCalendarOperationRequestSchema.Type;
export type FailCalendarOperationRequest = typeof FailCalendarOperationRequestSchema.Type;
export type ReconcileCalendarOperationRequest = typeof ReconcileCalendarOperationRequestSchema.Type;
export type CalendarSyncReason = typeof CalendarSyncReasonSchema.Type;
export type CalendarSyncSource = typeof CalendarSyncSourceSchema.Type;
export type CalendarSyncExternalEvent = typeof CalendarSyncExternalEventSchema.Type;
export type CalendarSyncDeletedEvent = typeof CalendarSyncDeletedEventSchema.Type;
export type CalendarSyncSnapshot = typeof CalendarSyncSnapshotSchema.Type;
export type CalendarSyncJobClaim = typeof CalendarSyncJobClaimSchema.Type;
export type ClaimCalendarSyncJobRequest = typeof ClaimCalendarSyncJobRequestSchema.Type;
export type ClaimCalendarSyncJobResult = typeof ClaimCalendarSyncJobResultSchema.Type;
export type CompleteCalendarSyncJobRequest = typeof CompleteCalendarSyncJobRequestSchema.Type;
export type FailCalendarSyncJobRequest = typeof FailCalendarSyncJobRequestSchema.Type;
export type CalendarSyncTransitionResult = typeof CalendarSyncTransitionResultSchema.Type;
